import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useLocation, Navigate } from 'react-router-dom'
import moment from 'moment'

import { Loader } from '@components'
import { useOrderRedux, useMembershipRedux } from '@hooks'
import { DINING_METHOD, ROUTES, customToast } from '@common'
import { ErrorTrackingAPI } from '@services'

const ForceRatingPage = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const { submitMemberOrder, isLoading: isOrderLoading } = useOrderRedux()
    const { pendingRatings, pendingRatingsLoading, pendingRatingsError, submitDishRatings } = useMembershipRedux()
    const [ratings, setRatings] = useState({}) // { [orderDishId]: { score: number, comment: string } }
    const [isSubmitting, setIsSubmitting] = useState(false)

    // Get order data from location state if exists (from checkout)
    const orderDataFromCheckout = location.state?.orderInput
    const diningMethodFromCheckout = location.state?.diningMethod
    const totalFromCheckout = location.state?.total

    // Get pending items from Redux (already fetched by CartFooter)
    const pendingItems = useMemo(() => pendingRatings?.pendingItems || [], [pendingRatings])

    // Check if user accessed directly (no location.state)
    const shouldRedirect = !location.state || !orderDataFromCheckout

    // Scroll to top when component mounts
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }, [])

    useEffect(() => {
        // Initialize ratings state when pending items are available
        if (pendingItems.length > 0) {
            const initialRatings = {}
            pendingItems.forEach(item => {
                initialRatings[item.orderDishId] = {
                    score: 0,
                    comment: '',
                }
            })
            setRatings(initialRatings)
        }
    }, [pendingItems])

    useEffect(() => {
        if (pendingRatingsError) {
            customToast.error(pendingRatingsError || 'Failed to fetch pending ratings')
        }
    }, [pendingRatingsError])

    const handleStarClick = (orderDishId, score) => {
        setRatings(prev => ({
            ...prev,
            [orderDishId]: {
                ...prev[orderDishId],
                score,
            },
        }))
    }

    const handleCommentChange = (orderDishId, comment) => {
        setRatings(prev => ({
            ...prev,
            [orderDishId]: {
                ...prev[orderDishId],
                comment,
            },
        }))
    }

    const isAllRated = () => {
        return pendingItems.every(item => {
            const rating = ratings[item.orderDishId]
            return rating && rating.score > 0
        })
    }

    const handleSubmit = async () => {
        if (!isAllRated()) {
            customToast.error('Please rate all items before submitting')
            return
        }

        setIsSubmitting(true)

        try {
            // Prepare ratings data with new schema format
            const ratingsArray = pendingItems.map(item => {
                const rating = ratings[item.orderDishId]
                return {
                    orderDishVariantIds: item.orderDishVariantIds.map(id => parseInt(id)),
                    score: rating.score,
                    comment: rating.comment || null,
                }
            })

            const submitResult = await submitDishRatings({
                ratings: ratingsArray,
            })

            if (submitResult.meta.requestStatus === 'fulfilled') {
                customToast.success('Ratings submitted successfully!')

                // Submit successful, proceed with order creation if order data exists
                if (orderDataFromCheckout) {
                    // Create order after ratings are completed
                    await createOrderAfterRating(orderDataFromCheckout, diningMethodFromCheckout, totalFromCheckout)
                } else {
                    // No order to create, redirect to home
                    customToast.success('All ratings completed!')
                    setIsSubmitting(false)
                    setTimeout(() => {
                        navigate(ROUTES.root, { replace: true })
                    }, 1000)
                }
            } else {
                customToast.error(submitResult.payload || 'Failed to submit ratings')
                setIsSubmitting(false)
            }
        } catch (error) {
            ErrorTrackingAPI.sendError(error, 'ForceRatingPage', 'submitRatings')
            customToast.error('An unexpected error occurred')
            setIsSubmitting(false)
        }
    }

    const createOrderAfterRating = async (orderInput, diningMethod, total) => {
        try {
            setIsSubmitting(true)
            const res = await submitMemberOrder(orderInput)

            if (res.meta.requestStatus === 'fulfilled') {
                customToast.success('Order created successfully!')

                // Navigate based on dining method
                if (diningMethod === DINING_METHOD.AdvanceTakeaway) {
                    // If final amount is $0.00 (free after credit discount), skip payment and go to order complete
                    if (total === 0) {
                        navigate(ROUTES.Order.complete.replace(':orderId', res.payload.orderId), { replace: true })
                    } else {
                        // Navigate to payment intent creation page for Advance Takeaway
                        navigate(ROUTES.AdvanceTakeawayPayment.root.replace(':orderId', res.payload.orderId), { replace: true })
                    }
                } else {
                    // Navigate to order complete page for other dining methods
                    navigate(ROUTES.Order.complete.replace(':orderId', res.payload.orderId), { replace: true })
                }
            } else {
                customToast.error('Submit Order Failure')
                customToast.error(res.payload)
            }
        } catch (error) {
            ErrorTrackingAPI.sendError(error, 'ForceRatingPage', 'createOrderAfterRating')
            customToast.error('Failed to create order after rating')
        } finally {
            setIsSubmitting(false)
        }
    }

    const formatOrderDate = dateString => {
        if (!dateString) return ''
        return moment(dateString).format('MMM DD, YYYY')
    }

    const renderStars = orderDishId => {
        const currentScore = ratings[orderDishId]?.score || 0
        const stars = []

        for (let i = 1; i <= 5; i++) {
            stars.push(
                <button
                    key={i}
                    type="button"
                    className="star-button"
                    onClick={() => handleStarClick(orderDishId, i)}
                    disabled={isSubmitting}
                >
                    <i className={`bx ${i <= currentScore ? 'bxs-star' : 'bx-star'}`}></i>
                </button>
            )
        }

        return <div className="stars-container">{stars}</div>
    }

    // If user accessed directly, redirect to home
    if (shouldRedirect) {
        return <Navigate to={ROUTES.root} replace />
    }

    if (pendingRatingsLoading || pendingItems.length === 0) {
        return <Loader />
    }

    return (
        <>
            {/* Show loading overlay when order is being created */}
            {isOrderLoading && <Loader />}
            <div className="rating-page">
                <div className="rating-container">
                    {/* Header */}
                    <div className="rating-header">
                        <h2 className="rating-title">Rate Your Recent Order</h2>
                        <p className="rating-subtitle">Please take a few seconds to provide your honest feedback</p>
                    </div>

                    {/* Error Message */}
                    {pendingRatingsError && (
                        <div className="alert alert-danger">
                            <i className="bx bx-error-circle"></i>
                            {pendingRatingsError}
                        </div>
                    )}

                    {/* Pending Items List */}
                    <div className="pending-items-list">
                        {pendingItems.map(item => {
                            const rating = ratings[item.orderDishId] || { score: 0, comment: '' }
                            const hasVariant = item.variantName && item.variantName !== 'Default Title'

                            return (
                                <div key={item.orderDishId} className="rating-item-card">
                                    <div className="rating-item-header">
                                        <div className="rating-item-info">
                                            <div className="rating-item-vendor-row">
                                                <h5 className="rating-item-vendor">{item.vendorName}</h5>
                                                <span className="order-date">
                                                    <i className="bx bx-calendar"></i>
                                                    {formatOrderDate(item.orderServingOn)}
                                                </span>
                                            </div>
                                            <div className="rating-item-title">
                                                <h5 className="rating-item-name">{item.dishName}</h5>
                                                {(hasVariant || item.quantity) && (
                                                    <div className="rating-item-variant-quantity-row">
                                                        {hasVariant && <p className="rating-item-variant">{item.variantName}</p>}
                                                        {item.quantity && (
                                                            <p className="rating-item-quantity">x {item.quantity}</p>
                                                        )}
                                                    </div>
                                                )}
                                                {item.selectionText && (
                                                    <p className="rating-item-selection-text">{item.selectionText}</p>
                                                )}
                                            </div>
                                            <div className="rating-item-meta">
                                                <div className="rating-section">{renderStars(item.orderDishId)}</div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="rating-item-body">
                                        <div className="comment-section">
                                            <label htmlFor={`comment-${item.orderDishId}`} className="comment-label">
                                                Share your thoughts about this (Optional)
                                            </label>
                                            <textarea
                                                id={`comment-${item.orderDishId}`}
                                                className="form-control comment-input"
                                                rows="3"
                                                placeholder="Share your thoughts about this dish..."
                                                value={rating.comment}
                                                onChange={e => handleCommentChange(item.orderDishId, e.target.value)}
                                                disabled={isSubmitting}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                    </div>

                    {/* Submit Button */}
                    <div className="rating-footer">
                        <button
                            type="button"
                            className="btn btn-primary btn-submit-ratings"
                            onClick={handleSubmit}
                            disabled={isSubmitting || !isAllRated()}
                        >
                            {isSubmitting ?
                                <>
                                    <i className="bx bx-loader-alt bx-spin"></i> Submitting...
                                </>
                            :   <>Complete Your Checkout</>}
                        </button>
                        {!isAllRated() && <p className="rating-hint">Please rate all items before submitting</p>}
                    </div>
                </div>
            </div>
        </>
    )
}

export default ForceRatingPage
